<script lang="ts" setup>
import { differenceInHours } from 'date-fns'

import { useProvideSpotimoodFetch } from '~/composables/useProvideSpotimoodFetch'

import { getDbTransaction } from '~/utils/IndexedDb/IndexedDb'

import { provideGetSpotifyPlaylistDetails } from '~/api-spotify-crawler/GetPlaylistDetails'

import type HtmlTag from '~/types/htmlTag'
import type { SpotimoodDetailCache } from '~/utils/IndexedDb/IndexedDb'

type Props = {
  forceFullHeight?: boolean
  hasSpotifyTheme?: boolean
  htmlTag?: HtmlTag
  small?: boolean
  spotifyPlaylistId: string
}
type Emits = {
  'iframe-click': []
}
type Slots = {
  default: (bind: {
    spotifyPlaylistId: string
    playlistInfos: SpotimoodDetailCache['details']
  }) => any
}

const emit = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  htmlTag: 'div',
  forceFullHeight: false,
  small: false,
  hasSpotifyTheme: false,
})
defineSlots<Slots>()

const { spotimoodFetch } = useProvideSpotimoodFetch()

const loading = ref(false)
const clickEventLatch = ref(false)
const playlistInfos = ref<SpotimoodDetailCache['details'] | null>(null)

onMounted(() => {
  window.focus()
  window.addEventListener('blur', handleNativeIframeClick)
  getPlaylistInfos().then((details) => (playlistInfos.value = details))
})
onBeforeUnmount(() =>
  window.removeEventListener('blur', handleNativeIframeClick),
)

async function checkIndexedDbForPlaylistDetails(): Promise<null | SpotimoodDetailCache> {
  try {
    const TTL_IN_HOURS = 2
    const currentTimeStamp = Date.now()
    const tx = await getDbTransaction('Spotimood', 1)
    const objectStore = tx.objectStore('apiCache', 'readwrite')
    const cachedValue = await objectStore.get(props.spotifyPlaylistId)

    if (!cachedValue) return null

    if (
      Math.abs(differenceInHours(currentTimeStamp, cachedValue.lastUpdated)) <
      TTL_IN_HOURS
    )
      return cachedValue

    await objectStore.delete(props.spotifyPlaylistId)
    return null
  } catch (_) {
    return null
  }
}

async function cachePlaylistDetailsInIndexedDb(
  details: SpotimoodDetailCache['details'],
): Promise<void> {
  try {
    const tx = await getDbTransaction('Spotimood', 1)
    const store = tx.objectStore('apiCache', 'readwrite')

    await store.put({
      details,
      id: props.spotifyPlaylistId,
      lastUpdated: Date.now(),
    })
  } catch (_) {
    // Intentionally empty
  }
}

async function getPlaylistInfos(): Promise<
  SpotimoodDetailCache['details'] | null
> {
  const cachedContent = await checkIndexedDbForPlaylistDetails()
  if (cachedContent) return cachedContent.details

  const getPlaylistDetails = provideGetSpotifyPlaylistDetails(spotimoodFetch)

  try {
    const details = await getPlaylistDetails(props.spotifyPlaylistId)
    if (!details) return null

    await cachePlaylistDetailsInIndexedDb(details)
    return details
  } catch (_) {
    return null
  }
}

function handleLoad() {
  loading.value = false
}

function handleNativeIframeClick(): void {
  setTimeout(() => {
    if (clickEventLatch.value) return

    if (document.activeElement?.id === props.spotifyPlaylistId) {
      clickEventLatch.value = true
      emit('iframe-click')
    }
  })
}
</script>

<template>
  <component
    :is="htmlTag"
    class="tw-relative"
    :class="{ 'tw-h-full': forceFullHeight }"
  >
    <div
      class="tw-relative tw-w-full"
      :class="{ 'tw-h-full': forceFullHeight }"
    >
      <ClientOnly>
        <template #fallback>
          <div class="tw-h-20"></div>
        </template>
        <div
          v-if="loading"
          key="loader"
          class="tw-loading-gradient tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full"
        />
        <iframe
          :id="spotifyPlaylistId"
          ref="iframe"
          :src="`https://open.spotify.com/embed/playlist/${spotifyPlaylistId}/${
            hasSpotifyTheme ? '' : '?theme=0'
          }`"
          width="100%"
          :height="small ? '80px' : '100%'"
          frameBorder="0"
          allowfullscreen="true"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen;
          picture-in-picture"
          class="tw-overflow-hidden"
          credentialless
          @load="handleLoad"
          @click.once="handleNativeIframeClick"
        />
      </ClientOnly>
    </div>
    <slot
      v-if="playlistInfos"
      name="default"
      v-bind="{ spotifyPlaylistId, playlistInfos }"
    />
  </component>
</template>
